import { Component, ChangeDetectionStrategy, OnInit, inject, input, output, computed, signal, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatChipsModule } from '@angular/material/chips';
import { MatFormFieldModule } from '@angular/material/form-field';
import { LTFilterRule, LTFilterColumn, LTFilterOperator } from '../../models/lt-filter.models';
import { LTFilterService } from '../../services/lt-filter.service';

interface LTFilterRowForm {
  field: FormControl<string>;
  operator: FormControl<LTFilterOperator>;
  value: FormControl<unknown>;
  ignoreCase: FormControl<boolean>;
}

@Component({
  selector: 'app-lt-filter-row',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatCheckboxModule,
    MatChipsModule,
    MatFormFieldModule,
    MatDatepickerModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './lt-filter-row.component.html',
  styleUrl: './lt-filter-row.component.scss',
  standalone: true,
  providers: [LTFilterService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class LTFilterRowComponent implements OnInit {
  private readonly ltFilterService = inject(LTFilterService);
  //#region Inputs
  readonly filter = input<LTFilterRule>();
  readonly columns = input.required<LTFilterColumn[]>();
  readonly logic = input<'and' | 'or'>('and');
  readonly showLogic = input(false);
  //#endregion Inputs
  
  //#region Outputs
  readonly filterChange = output<LTFilterRule>();
  readonly logicChange = output<'and' | 'or'>();
  readonly remove = output<void>();
  //#endregion Outputs

  //#region Form controls
  readonly form = new FormGroup<LTFilterRowForm>({
    field: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    operator: new FormControl<LTFilterOperator>('eq', { nonNullable: true }),
    value: new FormControl(null),
    ignoreCase: new FormControl(false, { nonNullable: true }),
  });

  readonly logicControl = new FormControl<'and' | 'or'>('and', { nonNullable: true });
  //#endregion Form controls

  //#region signals
  // Reactive signals for form values 
  readonly currentField = signal<string>('');
  readonly currentOperator = signal<string>('eq');

  // Signals for chip management
  readonly valueChips = signal<string[]>([]);

  //#endregion signals

  //#region Computed values
  readonly selectedColumn = computed(() => {
    const field = this.currentField();
    return this.columns().find(col => col.field === field);
  });

  readonly selectedOperator = computed(() => {
    const operator = this.currentOperator();
    const column = this.selectedColumn();
    if (!operator || !column) return null;
    
    return this.ltFilterService.getOperatorsForType(column.type)
      .find(op => op.operator === operator);
  });

  readonly availableOperators = computed(() => {
    const column = this.selectedColumn();
    return column ? this.ltFilterService.getOperatorsForType(column.type) : [];
  });

  readonly isInOperator = computed(() => {
    const operator = this.currentOperator();
    return operator === 'in' || operator === 'notin';
  });
  //#endregion Computed values

  ngOnInit(): void {
      // Initialize form with filter values if provided
    const currentFilter = this.filter();
    if (currentFilter) {
      this.form.patchValue({
        field: currentFilter.field,
        operator: currentFilter.operator,
        value: currentFilter.value,
        ignoreCase: currentFilter.ignoreCase ?? false,
      });

      // Update reactive signals
      this.currentField.set(currentFilter.field);
      this.currentOperator.set(currentFilter.operator);

      // Handle chip values for 'in' operators
      if ((currentFilter.operator === 'in' || currentFilter.operator === 'notin') && 
          Array.isArray(currentFilter.value)) 
      {
        this.valueChips.set(currentFilter.value.map(String));
      }
    }

    this.logicControl.patchValue(this.logic());

    // Watch form changes
    this.form.valueChanges.subscribe(() => this.emitFilterChange());
    this.logicControl.valueChanges.subscribe((value) => {
      if (value) this.logicChange.emit(value);
    });

    // watch field and update signal
    this.form.get('field')?.valueChanges.subscribe((fieldValue) => {
      if (fieldValue) {
        this.currentField.set(fieldValue);
        
        // Reset operator when field changes
        const operators = this.availableOperators();
        if (operators.length > 0) {
          const newOperator = operators[0].operator;
          this.form.get('operator')?.setValue(newOperator);
          this.currentOperator.set(newOperator);
        }
        this.form.get('value')?.setValue(null);
        this.valueChips.set([]);
      }
    });

    // Watch operator changes and update signal
    this.form.get('operator')?.valueChanges.subscribe((operatorValue) => {
      if (operatorValue) {
        this.currentOperator.set(operatorValue);
        
        // Clear value when operator changes to one that doesn't require it
        if (!this.selectedOperator()?.requiresValue) {
          this.form.get('value')?.setValue(null);
        }
        this.valueChips.set([]);
      }
    });
  }

  //#region Chip Functions

  addChip(event: { value: string, chipInput: { clear(): void } }): void {
    const value = event.value.trim();
    if (value) {
      const currentChips = this.valueChips();
      this.valueChips.set([...currentChips, value]);
      this.updateChipValue();
    }
    event.chipInput.clear();
  }

  addNumberChip(event: { value: string, chipInput: { clear(): void } }): void {
    const value = event.value.trim();
    const numValue = parseFloat(value);
    if (value && !isNaN(numValue)) {
      const currentChips = this.valueChips();
      this.valueChips.set([...currentChips, numValue.toString()]);
      this.updateChipValue();
    }
    event.chipInput.clear();
  }

  removeChip(chip: string): void {
    const currentChips = this.valueChips();
    this.valueChips.set(currentChips.filter(c => c !== chip));
    this.updateChipValue();
  }

  getSelectedCount(): number {
    const value = this.form.get('value')?.value;
    return Array.isArray(value) ? value.length : 0;
  }

  private updateChipValue(): void {
    const chips = this.valueChips();
    const column = this.selectedColumn();
    
    if (column?.type === 'number') {
      const numbers = chips.map(c => parseFloat(c)).filter(n => !isNaN(n));
      this.form.get('value')?.setValue(numbers);
    } else {
      this.form.get('value')?.setValue(chips);
    }
  }
  //#endregion Chip Functions

  private emitFilterChange(): void {
    if (this.form.invalid) return;

    //const formValue = this.form.value;
    const formValue = this.form.getRawValue();
    const selectedOp = this.selectedOperator();
    
    if (!formValue.field || !formValue.operator || !selectedOp) return;

    // For operators that don't require value, set value to null
    const value = selectedOp.requiresValue ? formValue.value : null;

    const filterDescriptor: LTFilterRule = {
      field: formValue.field,
      operator: formValue.operator,
      value,
      ignoreCase: formValue.ignoreCase,
    };

    this.filterChange.emit(filterDescriptor);
  }

}
